import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import MoreIcon from "@mui/icons-material/MoreVertRounded";
import AddIcon from "@mui/icons-material/AddOutlined";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { DataTable, type Column } from "@/components/data/data-table";
import { FilterSelect } from "@/components/data/filter-select";
import { StatusChip } from "@/components/display/status-chip";
import { MonoText, IdChip } from "@/components/display/mono";
import { RelativeTime } from "@/components/display/relative-time";
import { EmptyState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { useConfirm } from "@/components/feedback/confirm-dialog";
import { Form } from "@/components/form/form";
import { FormNumberField, FormSelect, FormTextField } from "@/components/form/fields";
import { useIsSystemAdmin } from "@/auth/use-permission";
import { type ProvisionResult, type Tenant, useTenantMutations, useTenants } from "@/api/tenants";

export const Route = createFileRoute("/_app/system/tenants/")({
  component: TenantsRegistry,
});

const PLANS = [
  { value: "FREE", label: "Free" },
  { value: "STANDARD", label: "Standard" },
  { value: "ENTERPRISE", label: "Enterprise" },
];

const createSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]{2,40}$/, "lowercase letters, digits and dashes"),
  name: z.string().min(2),
  plan: z.string().min(1),
  maxUsers: z.number().nullish(),
  maxWorkspaces: z.number().nullish(),
});

function TenantsRegistry() {
  const isAdmin = useIsSystemAdmin();
  const toast = useToast();
  const confirm = useConfirm();
  const { data, isLoading, error, refetch } = useTenants();
  const m = useTenantMutations();

  const [creating, setCreating] = useState(false);
  const [menu, setMenu] = useState<{ el: HTMLElement; row: Tenant } | null>(null);
  const [provisionFor, setProvisionFor] = useState<Tenant | null>(null);
  const [provisioned, setProvisioned] = useState<ProvisionResult | null>(null);
  const [plan, setPlan] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const rows = (data ?? []).filter(
    (t) =>
      (!plan || (t.plan ?? "").toUpperCase() === plan) &&
      (!statusFilter || (t.status ?? "ACTIVE").toUpperCase() === statusFilter),
  );

  const createForm = useForm<z.infer<typeof createSchema>>({
    resolver: zodResolver(createSchema),
    defaultValues: { id: "", name: "", plan: "STANDARD", maxUsers: null, maxWorkspaces: null },
  });
  const provisionForm = useForm<{ email: string }>({
    resolver: zodResolver(z.object({ email: z.string().email() })),
    defaultValues: { email: "" },
  });

  if (!isAdmin) {
    return (
      <PageContainer>
        <PageHeader title="Tenants" />
        <EmptyState
          title="System administrators only"
          description="The tenant registry is restricted to users with the SYSTEM_ADMIN role."
        />
      </PageContainer>
    );
  }

  const columns: Column<Tenant>[] = [
    {
      id: "name",
      header: "Tenant",
      cell: (r) => (
        <Stack spacing={0.25}>
          <span>{r.name}</span>
          <IdChip value={r.id} />
        </Stack>
      ),
      sortValue: (r) => r.name,
    },
    { id: "plan", header: "Plan", cell: (r) => r.plan ?? "—", nowrap: true },
    { id: "status", header: "Status", cell: (r) => <StatusChip status={r.status ?? "ACTIVE"} /> },
    {
      id: "users",
      header: "Users",
      align: "right",
      cell: (r) => (r.maxUsers ? `${r.userCount ?? 0} / ${r.maxUsers}` : (r.userCount ?? 0)),
    },
    { id: "created", header: "Created", cell: (r) => <RelativeTime value={r.createdAt} /> },
    {
      id: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <IconButton
          size="small"
          aria-label="Tenant actions"
          onClick={(e) => {
            e.stopPropagation();
            setMenu({ el: e.currentTarget, row: r });
          }}
        >
          <MoreIcon fontSize="inherit" />
        </IconButton>
      ),
    },
  ];

  async function onCreate(values: z.infer<typeof createSchema>) {
    await m.create.mutateAsync({
      id: values.id,
      name: values.name,
      plan: values.plan,
      maxUsers: values.maxUsers ?? undefined,
      maxWorkspaces: values.maxWorkspaces ?? undefined,
    });
    toast.success("Tenant created");
    setCreating(false);
    createForm.reset();
  }

  async function onProvision(values: { email: string }) {
    if (!provisionFor) return;
    const res = await m.provision.mutateAsync({ id: provisionFor.id, email: values.email });
    setProvisionFor(null);
    provisionForm.reset();
    setProvisioned(res);
  }

  const suspended = (menu?.row.status ?? "").toUpperCase() === "SUSPENDED";

  return (
    <PageContainer>
      <PageHeader
        title="Tenants"
        description="Provision and manage tenant organisations on this deployment."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            New tenant
          </Button>
        }
      />
      <Card>
        <DataTable
          columns={columns}
          data={isLoading || error ? undefined : rows}
          isLoading={isLoading}
          error={error}
          onRetry={() => refetch()}
          rowKey={(r) => r.id}
          search={(r) => `${r.name} ${r.id} ${r.plan ?? ""}`}
          searchPlaceholder="Search tenants…"
          filters={
            <>
              <FilterSelect
                label="Plan"
                value={plan}
                onChange={setPlan}
                options={["FREE", "STANDARD", "ENTERPRISE"]}
              />
              <FilterSelect
                label="Status"
                value={statusFilter}
                onChange={setStatusFilter}
                options={["ACTIVE", "SUSPENDED"]}
              />
            </>
          }
          empty={
            <EmptyState title="No tenants" description="Create the first tenant to get started." />
          }
        />
      </Card>

      <Menu anchorEl={menu?.el ?? null} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            if (menu) setProvisionFor(menu.row);
            setMenu(null);
          }}
        >
          Provision admin…
        </MenuItem>
        <MenuItem
          onClick={async () => {
            const row = menu?.row;
            setMenu(null);
            if (!row) return;
            await m.setActive.mutateAsync({ id: row.id, active: suspended });
            toast.success(suspended ? "Tenant activated" : "Tenant suspended");
          }}
        >
          {suspended ? "Activate" : "Suspend"}
        </MenuItem>
        <MenuItem
          sx={{ color: "error.main" }}
          onClick={async () => {
            const row = menu?.row;
            setMenu(null);
            if (!row) return;
            if (
              await confirm({
                title: `Delete tenant "${row.name}"?`,
                description: "This permanently removes the tenant and all of its data.",
                destructive: true,
                confirmLabel: "Delete tenant",
              })
            ) {
              await m.remove.mutateAsync(row.id);
              toast.success("Tenant deleted");
            }
          }}
        >
          Delete…
        </MenuItem>
      </Menu>

      <Dialog open={creating} onClose={() => setCreating(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New tenant</DialogTitle>
        <Form form={createForm} onSubmit={onCreate}>
          <DialogContent>
            <Stack spacing={2}>
              <FormTextField
                control={createForm.control}
                name="id"
                label="Tenant ID"
                required
                placeholder="acme"
                helperText="Immutable — used in URLs and headers."
              />
              <FormTextField
                control={createForm.control}
                name="name"
                label="Display name"
                required
              />
              <FormSelect control={createForm.control} name="plan" label="Plan" options={PLANS} />
              <Stack direction="row" spacing={2}>
                <FormNumberField control={createForm.control} name="maxUsers" label="Max users" />
                <FormNumberField
                  control={createForm.control}
                  name="maxWorkspaces"
                  label="Max workspaces"
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={createForm.formState.isSubmitting}>
              Create
            </Button>
          </DialogActions>
        </Form>
      </Dialog>

      <Dialog
        open={Boolean(provisionFor)}
        onClose={() => setProvisionFor(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Provision admin for {provisionFor?.name}</DialogTitle>
        <Form form={provisionForm} onSubmit={onProvision}>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Creates or resets the admin account and returns a one-time password.
            </DialogContentText>
            <FormTextField
              control={provisionForm.control}
              name="email"
              label="Admin email"
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setProvisionFor(null)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={provisionForm.formState.isSubmitting}
            >
              Provision
            </Button>
          </DialogActions>
        </Form>
      </Dialog>

      <Dialog
        open={Boolean(provisioned)}
        onClose={() => setProvisioned(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Admin provisioned</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this password now — it is not stored and cannot be shown again.
          </Alert>
          <Stack spacing={1}>
            <div>
              Email: <MonoText component="span">{provisioned?.email}</MonoText>
            </div>
            <div>
              Password: <MonoText component="span">{provisioned?.password}</MonoText>
            </div>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (provisioned?.password) {
                void navigator.clipboard?.writeText(provisioned.password).catch(() => {});
                toast.success("Password copied");
              }
            }}
          >
            Copy password
          </Button>
          <Button variant="contained" onClick={() => setProvisioned(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
